import { sendTxAndConfirm, TransactionError } from 'utils/governance/tools'
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddressSync,
} from '@solana/spl-token'
import {
  ComputeBudgetProgram,
  Connection,
  PublicKey,
  SystemProgram,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { WalletContextState } from '@solana/wallet-adapter-react'
import { toNative } from './tokens'
import { notify } from './notifications'
import { Market, UiWrapper } from '@cks-systems/manifest-sdk'
import { getDecimalCount } from './numbers'
import Big from 'big.js'
import { WRAPPED_SOL_MINT } from './constants'
import { getFeeEstimate } from './priorityFee'
import { isError } from 'lodash'
import {
  getSimulationComputeUnits,
  isSetComputeLimitInstruction,
} from './referrals'

export const MANIFEST_PROGRAM_ID = new PublicKey(
  'MNFSTqtC93rEfYHB6hF82sKdZpUDFWkViLByLd1k1Ms',
)

export const UI_WRAPPER_PROGRAM_ID = new PublicKey(
  'UMnFStVeG1ecZFc2gc5K3vFy3sMpotq8C91mXBQDGwh',
)

export const placeLimitOrder = async (
  baseMint: PublicKey,
  baseTokenProgram: string,
  baseDecimals: number,
  quoteMint: PublicKey,
  quoteTokenProgram: string,
  quoteDecimals: number,
  connection: Connection,
  walletPublicKey: PublicKey,
  signTransaction: NonNullable<WalletContextState['signTransaction']>,
  isBid: boolean,
  amount: string,
  price: string,
  isExistingMarket: boolean,
  translations: {
    transactionSent: string
    waitingForConfirmation: string
    transactionConfirmed: string
    transactionError: string
    tryAgain: string
  },
  successCallback: () => void,
) => {
  if (!walletPublicKey) {
    return
  }
  let txid: string | undefined
  const defaultComputeUnit = isExistingMarket ? 400000 : 500000
  try {
    const txFee = await getFeeEstimate(connection)
    let instructions = [
      ComputeBudgetProgram.setComputeUnitLimit({
        units: defaultComputeUnit,
      }),
      ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: txFee,
      }),
    ]

    const signers = []
    const needToCreateWSolAcc = !isBid
      ? baseMint.equals(WRAPPED_SOL_MINT)
      : quoteMint.equals(WRAPPED_SOL_MINT)

    if (needToCreateWSolAcc) {
      const wsolAta = getAssociatedTokenAddressSync(
        WRAPPED_SOL_MINT,
        walletPublicKey,
        true,
      )
      const createPayerAtaIx =
        createAssociatedTokenAccountIdempotentInstruction(
          walletPublicKey,
          wsolAta,
          walletPublicKey,
          WRAPPED_SOL_MINT,
        )
      const solTransferIx = SystemProgram.transfer({
        fromPubkey: walletPublicKey,
        toPubkey: wsolAta,
        lamports: toNative(
          Number(!isBid ? amount : Number(amount) * Number(price)),
          9,
        ).toNumber(),
      })

      const syncNative = createSyncNativeInstruction(wsolAta)
      instructions.push(createPayerAtaIx, solTransferIx, syncNative)
    }
    const placeOrderObj = await UiWrapper.placeOrderCreateIfNotExistsIxs(
      connection,
      baseMint,
      baseDecimals,
      quoteMint,
      quoteDecimals,
      walletPublicKey,
      walletPublicKey,
      {
        isBid,
        amount: Number(amount),
        price: Number(price),
        orderId: new Date().getTime(),
      },
      new PublicKey(baseTokenProgram),
      new PublicKey(quoteTokenProgram),
    )
    instructions.push(...placeOrderObj.ixs)
    signers.push(...placeOrderObj.signers)
    if (needToCreateWSolAcc) {
      const wsolAta = getAssociatedTokenAddressSync(
        WRAPPED_SOL_MINT,
        walletPublicKey,
        true,
      )
      const solTransferIx = createCloseAccountInstruction(
        wsolAta,
        walletPublicKey,
        walletPublicKey,
      )
      instructions.push(solTransferIx)
    }

    const units = await getSimulationComputeUnits(
      connection,
      instructions,
      walletPublicKey,
      [],
    )
    //replace compute unit with dynamic value
    instructions = [
      ...instructions.map((ix) => {
        if (isSetComputeLimitInstruction(ix)) {
          return ComputeBudgetProgram.setComputeUnitLimit({
            units: units || defaultComputeUnit,
          })
        }
        return ix
      }),
    ]

    const block = await connection.getLatestBlockhash('finalized')

    const vtx = new VersionedTransaction(
      new TransactionMessage({
        payerKey: walletPublicKey,
        instructions: instructions,
        recentBlockhash: block.blockhash,
      }).compileToV0Message(),
    )

    if (signers.length) {
      vtx.sign(signers)
    }
    const signed = await signTransaction(vtx)
    txid = await sendTxAndConfirm(connection, signed, block, {
      transactionSent: translations.transactionSent,
      waitingForConfirmation: translations.waitingForConfirmation,
    })

    notify({
      title: translations.transactionConfirmed,
      type: 'success',
      txid,
    })
    successCallback()
  } catch (e: unknown) {
    console.error('error placing limit order', e)
    notify({
      title: translations.transactionError,
      description:
        e != null
          ? e instanceof TransactionError || isError(e)
            ? e.message
            : String(e)
          : translations.tryAgain,
      type: 'error',
      txid: txid != null ? txid : e instanceof TransactionError ? e.txid : '',
    })
  }
}

export type MarketInfo = {
  baseMint: string
  quoteMint: string
  marketAddress: string
}

export const fetchMarketMints = async (
  conn: Connection,
  marketAddrs: Array<string>,
) => {
  const markets: Market[] = []
  await Promise.all(
    marketAddrs.map(async (ma) => {
      const m = await Market.loadFromAddress({
        connection: conn,
        address: new PublicKey(ma),
      })
      markets.push(m)
    }),
  )

  const marketsInfo: MarketInfo[] = []
  for (const m of markets) {
    const info = {
      baseMint: m.baseMint().toBase58(),
      quoteMint: m.quoteMint().toBase58(),
      marketAddress: m.address.toBase58(),
    }
    marketsInfo.push(info)
  }

  return marketsInfo
}

export const fetchMarket = (
  conn: Connection,
  marketAddress: PublicKey,
): Promise<Market> => {
  return Market.loadFromAddress({
    connection: conn,
    address: new PublicKey(marketAddress),
  })
}

export type OrdersKind = 'asks' | 'bids'

export const groupOrdersBy = (
  grouping: number,
  ordersKind: OrdersKind,
  ordersArray: readonly (readonly [orderPrice: number, orderSize: number])[],
) => {
  const groupingDecimals = getDecimalCount(grouping)
  if (!ordersArray || !grouping) {
    return ordersArray || []
  }
  const groupFloors = new Map<number, number>()
  for (let i = 0; i < ordersArray.length; i++) {
    if (ordersArray[i] == null) {
      continue
    }
    const bigGrouping = Big(grouping)
    const bigOrderPrice = Big(ordersArray[i][0])

    const bigFloor = bigOrderPrice
      .div(bigGrouping)
      .round(0, ordersKind === 'bids' ? Big.roundDown : Big.roundUp)
      .times(bigGrouping)

    const floor = Number(bigFloor.toFixed(groupingDecimals))
    groupFloors.set(
      floor,
      (groupFloors.get(floor) ?? 0) + Number(ordersArray[i][1]),
    )
  }
  return [...groupFloors].sort((a, b) => {
    if (!a || !b) {
      return -1
    }
    return b[0] - a[0]
  })
}

export interface LimitOrder {
  sizePercent: number
  averagePrice: number
  cumulativeValue: number
  cumulativeSize: number
  cumulativeSizePercent: number
  price: number
  size: number
}

export const processLimitOrders = (
  ordersKind: OrdersKind,
  orders: readonly (readonly [orderPrice: number, orderSize: number])[],
  depth: number,
): LimitOrder[] => {
  let cumulativeSize = 0
  let cumulativeValue = 0
  let cumulativeSizePercent = 0

  const ordersToProcess =
    ordersKind === 'asks'
      ? orders.slice(-depth, orders.length)
      : orders.slice(0, depth)

  const totalForPercent =
    ordersKind === 'asks'
      ? orders.reduce((total, [, size]) => total + size, 0) // raw size
      : orders.reduce((total, [price, size]) => total + price * size, 0) // notional

  const result = Array.from(
    { length: ordersToProcess.length },
    () => ({}),
  ) as LimitOrder[]

  for (let i = 0; i < ordersToProcess.length; i++) {
    const index = ordersKind === 'asks' ? ordersToProcess.length - 1 - i : i
    const [orderPrice, orderSize] = ordersToProcess[index]

    const notional = orderPrice * orderSize
    const valueForPercent = ordersKind === 'asks' ? orderSize : notional
    const sizePercent = (valueForPercent / (totalForPercent || 1)) * 100

    cumulativeSize += orderSize
    cumulativeValue += notional
    cumulativeSizePercent += sizePercent

    result[index] = {
      price: orderPrice,
      size: orderSize,
      sizePercent,
      averagePrice: cumulativeValue / cumulativeSize,
      cumulativeValue,
      cumulativeSize,
      cumulativeSizePercent,
    }
  }

  return result
}

export function formatPrice(n: number): string {
  const decimals = Math.max(0, Math.floor(9 - Math.log10(n)))
  return Intl.NumberFormat('en', {
    minimumSignificantDigits: decimals,
    maximumSignificantDigits: decimals,
  }).format(n)
}
